import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseclient";

export const PLAN_FEATURES = {
  basic: {
    label: "Basic",
    price: 299,
    patient_limit: null,
    therapist_limit: 4,
    features: [
      "dashboard", "patients", "therapists", "schedule",
      "diagnostics", "histories", "attachments", "settings",
      "knowledge", "outcomes", "analytics",
    ],
  },
  pro: {
    label: "Pro",
    price: 599,
    patient_limit: null,
    therapist_limit: 8,
    features: [
      "dashboard", "patients", "therapists", "schedule",
      "diagnostics", "histories", "attachments", "settings",
      "knowledge", "outcomes", "analytics",
      "progress", "billing", "soap",
    ],
  },
  plus: {
    label: "Plus",
    price: 899,
    patient_limit: null,
    therapist_limit: null,
    features: [
      "dashboard", "patients", "therapists", "schedule",
      "diagnostics", "histories", "attachments", "settings",
      "knowledge", "outcomes", "analytics",
      "progress", "billing", "soap",
      "research", "export", "multi_branch",
      "custom_branding", "priority_support",
    ],
  },
};

export const FEATURE_LABELS = {
  dashboard: "Dashboard",
  patients: "Patient Registry",
  therapists: "Therapist Management",
  schedule: "Appointment Schedule",
  diagnostics: "Diagnostics",
  histories: "Patient Histories",
  attachments: "Attachments",
  settings: "Clinic Settings",
  knowledge: "Knowledge Hub",
  outcomes: "Outcome Measures",
  analytics: "Analytics",
  progress: "Progress Analytics",
  billing: "Billing & Payments",
  soap: "SOAP Note Generator",
  research: "Research Module",
  export: "CSV Data Export",
  multi_branch: "Multi-branch Support",
  custom_branding: "Custom Branding",
  priority_support: "Priority Support",
};

export const LOCKED_FEATURE_INFO = {
  analytics: {
    title: "Analytics",
    description: "Unlock clinic analytics, pain score distribution, and therapist performance insights.",
    icon: "📊",
    minPlan: "basic",
  },
  progress: {
    title: "Progress Analytics",
    description: "Track pain scores, ROM, and strength over time with professional progress analytics.",
    icon: "📈",
    minPlan: "pro",
  },
  billing: {
    title: "Billing & Payments",
    description: "Unlock billing summary, payment tracking, and revenue insights.",
    icon: "₹",
    minPlan: "pro",
  },
  soap: {
    title: "SOAP Notes AI",
    description: "Generate structured SOAP notes quickly using AI assistance.",
    icon: "📝",
    minPlan: "pro",
  },
  research: {
    title: "Research Module",
    description: "Create studies, collect data, and export for research analysis.",
    icon: "🔬",
    minPlan: "plus",
  },
  export: {
    title: "CSV Export",
    description: "Export your clinic and patient data to CSV for external analysis.",
    icon: "📥",
    minPlan: "plus",
  },
};

const TRIAL_FEATURES = [
  "dashboard", "patients", "therapists", "schedule",
  "diagnostics", "histories", "attachments", "settings",
  "knowledge", "outcomes", "analytics", "progress",
  "billing", "soap", "research", "export",
  "multi_branch", "custom_branding", "priority_support",
];

export function usePlan(session, organizationId) {   // ✅ CHANGED: added organizationId param
  const [planData, setPlanData]       = useState(null);
  const [trialData, setTrialData]     = useState(null);
  const [loading, setLoading]         = useState(true);
  const [isAdmin, setIsAdmin]         = useState(false);
  const [profileRole, setProfileRole] = useState("user");

  const fetchPlanData = useCallback(async () => {
    if (!session?.user?.id || !organizationId) {   // ✅ CHANGED: wait for organizationId too
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [planResult, profileResult, trialResult] = await Promise.all([
        supabase
          .from("user_current_plan")
          .select("*")
          .eq("organization_id", organizationId)    // ✅ CHANGED: filter by org, not user
          .maybeSingle(),

        supabase
          .from("profiles")
          .select("role, is_admin")
          .eq("id", session.user.id)
          .maybeSingle(),

        supabase
          .from("user_trials")
          .select("*")
          .eq("organization_id", organizationId)    // ✅ CHANGED: filter by org, not user
          .maybeSingle(),
      ]);

      const profileData = profileResult.data;
      const adminStatus = profileData?.is_admin === true 
        || profileData?.role === "admin";
      
      setIsAdmin(adminStatus);
      setProfileRole(profileData?.role || "user");
      setPlanData(planResult.data);

      let trial = trialResult.data;
      if (!trial) {
        const now = new Date();
        const endDate = new Date(now);
        endDate.setDate(endDate.getDate() + 14);

        const { data: newTrial, error: trialErr } = await supabase
          .from("user_trials")
          .upsert([{
            user_id: session.user.id,
            organization_id: organizationId,         // ✅ CHANGED: added organization_id
            trial_start_date: now.toISOString(),
            trial_end_date: endDate.toISOString(),
            created_at: now.toISOString(),
          }], { onConflict: "organization_id" })      // ✅ CHANGED: conflict target is org now
          .select()
          .maybeSingle();

        if (trialErr) {
          console.warn("Trial insert warning:", trialErr.message);
          const { data: retryTrial } = await supabase
            .from("user_trials")
            .select("*")
            .eq("organization_id", organizationId)
            .maybeSingle();
          trial = retryTrial;
        } else {
          trial = newTrial;
        }
      }

      setTrialData(trial);

    } catch (err) {
      console.error("usePlan fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id, organizationId]);   // ✅ CHANGED: dependency includes organizationId

  useEffect(() => {
    fetchPlanData();
  }, [fetchPlanData]);

  // ── Trial calculations ──────────────────────────────────────────
  const trialEndDate = trialData?.trial_end_date
    ? new Date(trialData.trial_end_date)
    : null;

  const trialDaysLeft = trialEndDate
    ? Math.max(0, Math.ceil(
        (trialEndDate - new Date()) / (1000 * 60 * 60 * 24)
      ))
    : 0;

  const isTrialActive  = !!trialEndDate && trialDaysLeft > 0;
  const isTrialExpired = !!trialEndDate && trialDaysLeft === 0;

  // ── Plan resolution ─────────────────────────────────────────────
  const basePlanName = planData?.plan_name || "free";
  const hasValidPlan = !!PLAN_FEATURES[basePlanName];

  const subscriptionRequired = !isTrialActive && !hasValidPlan && !isAdmin;

  const activePlanConfig = isAdmin
    ? { features: TRIAL_FEATURES, therapist_limit: null, patient_limit: null }
    : isTrialActive
      ? { 
          label: "Free Trial",
          price: 0,
          features: TRIAL_FEATURES,
          therapist_limit: null,
          patient_limit: null,
        }
      : PLAN_FEATURES[basePlanName] || null;

  function hasFeature(featureKey) {
    if (isAdmin) return true;
    if (isTrialActive) return true;
    if (subscriptionRequired) return false;
    return activePlanConfig?.features?.includes(featureKey) ?? false;
  }

  const planName = isAdmin
    ? "admin"
    : isTrialActive
      ? "free_trial"
      : basePlanName;

  return {
    planName,
    basePlanName,
    planData,
    trialData,
    trialDaysLeft,
    isTrialActive,
    isTrialExpired,
    subscriptionRequired,
    activePlanConfig,
    hasFeature,
    isAdmin,
    profileRole,
    therapistLimit: isAdmin ? null : activePlanConfig?.therapist_limit ?? null,
    patientLimit:   isAdmin ? null : activePlanConfig?.patient_limit   ?? null,
    loading,
    refetch: fetchPlanData,
  };
}